import { Box, Button, Checkbox, Flex, HStack, MenuItem, Td, Tooltip, Tr } from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { FiCalendar, FiCheckCircle, FiRepeat } from "react-icons/fi";
import { VThreeDotMenu } from "components/Menu/ThreeDotMenu";
import { AttendeeAvatar, AttendeeNameTag } from "components/Tag/ResourceTargetTag/MeetingAttendeeTag";
import { PlusNTag } from "components/Tag/PlusNTag";
import TwoTierPopoverTagColumn from "components/Column/TwoTierPopoverTagDisplay";
import CDS, { AdminMeetingTypeTag } from "design-system";
import { dateTo12hrTimeOfDayWithTZInZone } from "helpers/time";
import MeetingLeadCell from "features/Resources/Meeting/Components/MeetingLeadCell";
import { BulkScheduleAttendee, BulkScheduleMeeting, MeetingSelection } from "../models";
import { leadFromAttendees } from "../helpers";

interface BulkScheduleMeetingRowProps {
  meeting: BulkScheduleMeeting;
  selection: MeetingSelection;
  onToggle: (meetingId: number) => void;
  onAdjust: (meetingId: number) => void;
  onSkip?: (meetingId: number) => void;
  onUnskip?: (meetingId: number) => void;
  onViewDetails?: (meeting: BulkScheduleMeeting) => void;
  onLeadChanged?: () => void;
  isConfirming: boolean;
}

function formatTimeRange(startIso: string, endIso: string): string {
  const opts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
  const start = new Date(startIso).toLocaleString(undefined, opts);
  const end = new Date(endIso).toLocaleString(undefined, opts);
  return `${start} – ${end}`;
}

const TIME_SLOT_RANGES: Record<string, { start: number; end: number }> = {
  morning: { start: 9, end: 12 },
  midday: { start: 11, end: 14 },
  afternoon: { start: 14, end: 17 },
};

function getHourInTimezone(isoString: string, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    hour12: false,
    timeZone: timezone,
  }).formatToParts(new Date(isoString));
  return Number(parts.find((p) => p.type === "hour")?.value ?? 0);
}

export function isTimeConflict(
  meeting: Pick<BulkScheduleMeeting, "proposedAt" | "useSpecificTime" | "preferredTimeSlot" | "timezone">,
  reservationStart: string,
): boolean {
  if (meeting.useSpecificTime) {
    if (!meeting.proposedAt) return false;
    return Math.abs(new Date(meeting.proposedAt).getTime() - new Date(reservationStart).getTime()) > 15 * 60 * 1000;
  }

  const range = TIME_SLOT_RANGES[meeting.preferredTimeSlot];
  if (!range) return false;

  const hour = getHourInTimezone(reservationStart, meeting.timezone);
  return hour < range.start || hour >= range.end;
}

const MAX_AVATARS = 3;

function AttendeeAvatarGroup({ attendees }: { attendees: BulkScheduleAttendee[] }) {
  const displaySummary = () => (
    <Flex align="center" flexDirection="row-reverse">
      <PlusNTag maxCount={MAX_AVATARS} items={attendees} ml="-6px" size="33px" />
      {attendees
        .slice(0, MAX_AVATARS)
        .reverse()
        .map((a) => (
          <Box key={a.id ?? a.email} ml="-6px">
            <AttendeeAvatar name={a.name ?? undefined} src={a.avatarUrl ?? undefined} />
          </Box>
        ))}
    </Flex>
  );

  const renderTag = (a: BulkScheduleAttendee) => (
    <AttendeeNameTag key={a.id ?? a.email} name={a.name ?? a.email ?? undefined} disabled />
  );

  return (
    <TwoTierPopoverTagColumn
      primaryItems={attendees}
      secondaryItems={[]}
      renderTag={renderTag}
      displaySummary={displaySummary}
      emptyLabel="No Attendees"
      keyFn={(a) => String(a.id ?? a.email)}
    />
  );
}

type TimeVariant = "adjusted" | "conflict" | "normal";

const TIME_SLOT_LABELS: Record<string, string> = {
  morning: "morning",
  midday: "midday",
  afternoon: "afternoon",
};

export const TIME_SLOT_DESCRIPTIONS: Record<string, string> = {
  morning: "9am – 12pm",
  midday: "11am – 2pm",
  afternoon: "2pm – 5pm",
};

function TimeDisplay({
  variant,
  startTime,
  endTime,
  useSpecificTime,
  preferredTimeSlot,
  proposedAt,
  meetingTimezone,
  onClick,
  isDisabled,
}: {
  variant: TimeVariant;
  startTime: string;
  endTime: string;
  useSpecificTime: boolean;
  preferredTimeSlot: string;
  proposedAt: string | null;
  meetingTimezone: string;
  onClick: () => void;
  isDisabled: boolean;
}) {
  const timeText = formatTimeRange(startTime, endTime);

  const content = (
    <Button
      variant="unstyled"
      height="auto"
      minW={0}
      p={0}
      onClick={onClick}
      isDisabled={isDisabled}
      _disabled={{ opacity: 0.6, cursor: "not-allowed" }}
    >
      <HStack spacing={variant === "conflict" ? 1 : 2}>
        {variant === "conflict" ? (
          <WarningTwoIcon color="orange.400" boxSize={3} />
        ) : (
          <Box color={variant === "adjusted" ? "green.500" : "green.400"}>
            <FiCheckCircle size={16} />
          </Box>
        )}
        <CDS.Text12
          color={variant === "adjusted" ? "green.600" : variant === "conflict" ? "orange.500" : undefined}
          fontWeight={variant !== "normal" ? "medium" : undefined}
        >
          {timeText}
        </CDS.Text12>
      </HStack>
    </Button>
  );

  if (variant === "conflict") {
    const tzAbbrev =
      new Intl.DateTimeFormat("en-US", { timeZone: meetingTimezone, timeZoneName: "short" })
        .formatToParts(new Date(startTime))
        .find((p) => p.type === "timeZoneName")?.value ?? meetingTimezone;

    const tooltipLabel =
      useSpecificTime && proposedAt
        ? `Differs from the requested time of ${dateTo12hrTimeOfDayWithTZInZone(proposedAt, meetingTimezone)} due to a scheduling conflict. Click to adjust.`
        : `Falls outside the preferred ${TIME_SLOT_LABELS[preferredTimeSlot] ?? preferredTimeSlot} window (${TIME_SLOT_DESCRIPTIONS[preferredTimeSlot] ?? ""}) in ${tzAbbrev}. Click to adjust.`;

    return (
      <Tooltip label={tooltipLabel} placement="top" hasArrow>
        {content}
      </Tooltip>
    );
  }

  return content;
}

export default function BulkScheduleMeetingRow({
  meeting,
  selection,
  onToggle,
  onAdjust,
  onSkip,
  onUnskip,
  onViewDetails,
  onLeadChanged,
  isConfirming,
}: BulkScheduleMeetingRowProps) {
  const { reservation, isRecurring } = meeting;
  const isSkipped = meeting.status === "skipped";
  const hasConflict =
    !isSkipped &&
    !isRecurring &&
    !selection.isAdjusted &&
    !!reservation &&
    isTimeConflict(meeting, reservation.startTime);

  const variant: TimeVariant = selection.isAdjusted ? "adjusted" : hasConflict ? "conflict" : "normal";

  return (
    <Tr _hover={{ bg: "bg.surface" }} borderBottom="1px solid" borderColor="bg.subtle" opacity={isSkipped ? 0.6 : 1}>
      {/* Checkbox */}
      <Td width="52px" px={4} py={3}>
        <Tooltip label="This meeting has been skipped" placement="top" hasArrow isDisabled={!isSkipped}>
          <Box>
            <Checkbox
              isChecked={isSkipped ? false : selection.included}
              onChange={() => onToggle(meeting.meetingId)}
              isDisabled={isConfirming || !reservation || isSkipped}
              colorScheme="blue"
            />
          </Box>
        </Tooltip>
      </Td>

      {/* When */}
      <Td px={3} py={3} minW="200px">
        {isSkipped ? (
          <CDS.Text12 color="gray.400" fontStyle="italic">
            Skipped
          </CDS.Text12>
        ) : reservation ? (
          <HStack spacing={1}>
            {isRecurring ? (
              <Button
                variant="unstyled"
                height="auto"
                minW={0}
                p={0}
                onClick={() => onAdjust(meeting.meetingId)}
                isDisabled={isConfirming}
                _disabled={{ opacity: 0.6, cursor: "not-allowed" }}
              >
                <HStack spacing={2}>
                  <Box color="blue.500">
                    <FiRepeat size={14} />
                  </Box>
                  <CDS.Text12 color="blue.600" fontWeight="medium">
                    {formatTimeRange(reservation.startTime, reservation.endTime)}
                  </CDS.Text12>
                </HStack>
              </Button>
            ) : (
              <TimeDisplay
                variant={variant}
                startTime={reservation.startTime}
                endTime={reservation.endTime}
                useSpecificTime={meeting.useSpecificTime}
                preferredTimeSlot={meeting.preferredTimeSlot}
                proposedAt={meeting.proposedAt}
                meetingTimezone={meeting.timezone}
                onClick={() => onAdjust(meeting.meetingId)}
                isDisabled={isConfirming}
              />
            )}
            <Button
              aria-label={isRecurring ? "Change session" : "Adjust time"}
              size="xs"
              variant="ghost"
              color="gray.500"
              onClick={() => onAdjust(meeting.meetingId)}
              isDisabled={isConfirming}
              minW={0}
              px={1}
            >
              <FiCalendar />
            </Button>
          </HStack>
        ) : (
          <CDS.Text12 color="gray.400" fontStyle="italic">
            {isRecurring ? "No session found" : "No time found"}
          </CDS.Text12>
        )}
      </Td>

      {/* Meeting Name */}
      <Td px={3} py={3}>
        <HStack spacing={2} align="center">
          <Box flexShrink={0}>
            <AdminMeetingTypeTag.IconBadge meetingType={meeting.meetingType as any} size="sm" />
          </Box>
          <Button
            variant="unstyled"
            height="auto"
            minW={0}
            p={0}
            onClick={() => onViewDetails?.(meeting)}
            textAlign="left"
          >
            <CDS.Text12
              fontWeight="medium"
              _hover={{ color: "blue.600" }}
              textDecorationLine={isSkipped ? "line-through" : "none"}
            >
              {meeting.meetingName}
            </CDS.Text12>
          </Button>
        </HStack>
      </Td>

      {/* Lead */}
      <Td px={3} py={3}>
        <MeetingLeadCell
          meetingId={String(meeting.meetingId)}
          meetingType={meeting.meetingType}
          status={meeting.status}
          lead={leadFromAttendees(meeting.attendees)}
          onLeadChanged={onLeadChanged}
        />
      </Td>

      {/* Attendees */}
      <Td px={3} py={3}>
        <AttendeeAvatarGroup attendees={meeting.attendees} />
      </Td>

      {/* Actions */}
      <Td px={3} py={3} textAlign="right">
        <VThreeDotMenu size="xs" ariaLabel="Meeting options" inPortal>
          {isSkipped
            ? onUnskip && <MenuItem onClick={() => onUnskip(meeting.meetingId)}>Unskip</MenuItem>
            : onSkip && <MenuItem onClick={() => onSkip(meeting.meetingId)}>Skip</MenuItem>}
        </VThreeDotMenu>
      </Td>
    </Tr>
  );
}
